import React from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/app/PageHeader";
import { toast } from "sonner";
import { LcbFtFormDialog } from "@/components/app/LcbFtFormDialog";
import { LcbFtDetail, RISK_BADGE } from "@/components/app/LcbFtDetail";

const fetcher = (u) => api.get(u).then((r) => r.data);

const EMPTY_FORM = {
  client_id: "",
  beneficial_owners_identified: false,
  id_documents_collected: false,
  pep_status: false,
  sanctions_check: false,
  activity_sector_risk: "faible",
  geographic_risk: "faible",
  cash_intensive: false,
  complex_structure: false,
  vigilance_measures: "",
  next_review_date: "",
};

function AssessmentList({ items, selected, onSelect, clientMap }) {
  return (
    <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
      <div className="p-4 border-b border-zinc-200 text-[10px] uppercase tracking-[0.22em] text-zinc-500">Évaluations</div>
      <ul className="divide-y divide-zinc-100 max-h-[70vh] overflow-y-auto">
        {items.length === 0 && <li className="p-6 text-sm text-zinc-500">Aucune évaluation.</li>}
        {items.map((it) => {
          const b = RISK_BADGE[it.risk_level] || RISK_BADGE.standard;
          return (
            <li key={it.id}>
              <button data-testid={`lcbft-item-${it.id}`} onClick={() => onSelect(it)} className={`w-full text-left p-4 hover:bg-zinc-50 ${selected?.id === it.id ? "bg-zinc-50" : ""}`}>
                <div className="text-sm font-medium truncate">{clientMap[it.client_id]?.name || "Client"}</div>
                <div className="mt-1 inline-block text-[10px] px-2 py-0.5 rounded-full" style={{ background: b.bg, color: b.fg }}>{b.label}</div>
                <div className="text-[11px] text-zinc-400 mt-1">{new Date(it.created_at).toLocaleString("fr-FR")}</div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function LcbFt() {
  const { data: items = [] } = useSWR("/lcbft/assessments", fetcher);
  const { data: clients = [] } = useSWR("/clients", fetcher);
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, next_review_date: form.next_review_date || null, vigilance_measures: form.vigilance_measures || null };
      const r = await api.post("/lcbft/assessments", payload);
      toast.success("Évaluation enregistrée — analyse IA en cours");
      setOpen(false);
      setSelected(r.data);
      mutate("/lcbft/assessments");
    } catch { toast.error("Enregistrement impossible"); }
    finally { setSubmitting(false); }
  };

  // Poll pendant la génération de l'analyse IA
  const lcbPollKey = selected && !selected.ai_analysis ? `/lcbft/assessments/${selected.id}` : null;
  useSWR(lcbPollKey, fetcher, { refreshInterval: 2500, onSuccess: (d) => setSelected(d) });

  return (
    <div>
      <PageHeader
        eyebrow="Conformité"
        title="LCB-FT"
        description="Questionnaire d'entrée en relation, profil de risque et diligences — conformément aux articles L.561-1 et s. du CMF."
        actions={
          <LcbFtFormDialog
            open={open} setOpen={setOpen}
            form={form} setForm={setForm}
            clients={clients} onSubmit={submit} submitting={submitting}
          />
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <AssessmentList items={items} selected={selected} onSelect={setSelected} clientMap={clientMap} />
        <div className="lg:col-span-2">
          <LcbFtDetail selected={selected} clientMap={clientMap} />
        </div>
      </div>
    </div>
  );
}

